import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Star, X, Upload, Edit3, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { uploadFile } from "../lib/uploads";

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { title: "", summary: "", link: "", images: [], main_image_url: "" };

const Portfolio = ({ userId, isMine }) => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // project object being edited (or "new")
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/portfolios/user/${userId}`);
      const d = await r.json();
      setProjects(d.projects || []);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setForm(emptyForm);
    setEditing("new");
    setErr("");
  };

  const startEdit = (p) => {
    setForm({
      title: p.title,
      summary: p.summary,
      link: p.link || "",
      images: p.images || [],
      main_image_url: p.main_image_url || "",
    });
    setEditing(p.id);
    setErr("");
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onImages = async (e) => {
    setErr("");
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const added = [];
      for (const f of files) {
        const up = await uploadFile(f, token);
        added.push({ url: up.url, path: up.path, caption: "" });
      }
      setForm((f) => ({
        ...f,
        images: [...f.images, ...added],
        main_image_url: f.main_image_url || added[0]?.url || "",
      }));
    } catch (ex) {
      setErr(ex.message || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((f) => {
      const next = f.images.filter((_, i) => i !== idx);
      const removed = f.images[idx]?.url;
      return {
        ...f,
        images: next,
        main_image_url: f.main_image_url === removed ? next[0]?.url || "" : f.main_image_url,
      };
    });
  };

  const save = async () => {
    setErr("");
    if (!form.title.trim()) {
      setErr("Give the project a title.");
      return;
    }
    setBusy(true);
    try {
      const body = { ...form };
      const path = editing === "new" ? `${API}/api/portfolios` : `${API}/api/portfolios/${editing}`;
      const method = editing === "new" ? "POST" : "PATCH";
      const r = await fetch(path, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Save failed");
      setEditing(null);
      await load();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    setBusy(true);
    await fetch(`${API}/api/portfolios/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    await load();
    setBusy(false);
  };

  return (
    <section className="portfolio-section" data-testid="portfolio-section">
      <header className="portfolio-header">
        <h2>Portfolio</h2>
        {isMine && !editing && (
          <button className="btn-solid" onClick={startNew} data-testid="portfolio-add-btn">
            <Plus size={16} /> Add project
          </button>
        )}
      </header>

      {editing && (
        <div className="portfolio-editor" data-testid="portfolio-editor">
          <label className="auth-field">
            <span>Title*</span>
            <input value={form.title} onChange={set("title")} data-testid="portfolio-title" />
          </label>
          <label className="auth-field">
            <span>Summary</span>
            <textarea rows={4} value={form.summary} onChange={set("summary")} data-testid="portfolio-summary" />
          </label>
          <label className="auth-field">
            <span>Link (optional)</span>
            <input value={form.link} onChange={set("link")} placeholder="https://…" />
          </label>
          <div className="portfolio-upload-block">
            <label className="btn-ghost portfolio-upload-btn" data-testid="portfolio-upload">
              <Upload size={16} /> Add images
              <input type="file" accept="image/*" multiple hidden onChange={onImages} />
            </label>
            <div className="portfolio-thumb-grid">
              {form.images.map((img, idx) => {
                const isMain = img.url === form.main_image_url;
                return (
                  <div
                    key={idx}
                    className={`portfolio-thumb${isMain ? " is-main" : ""}`}
                    data-testid={`portfolio-thumb-${idx}`}
                  >
                    <img src={img.url} alt="" />
                    <div className="portfolio-thumb-actions">
                      <button
                        type="button"
                        title={isMain ? "Main image" : "Set as main"}
                        onClick={() => setForm((f) => ({ ...f, main_image_url: img.url }))}
                        className={isMain ? "chip-star active" : "chip-star"}
                        data-testid={`portfolio-set-main-${idx}`}
                      >
                        <Star size={14} />
                      </button>
                      <button type="button" className="chip-x" onClick={() => removeImage(idx)} aria-label="remove">
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="portfolio-hint">Tap the ★ to pick the main image (shown on your profile card).</p>
          </div>

          {err && <div className="auth-error" data-testid="portfolio-error">{err}</div>}

          <div className="portfolio-editor-actions">
            <button className="btn-solid" onClick={save} disabled={busy} data-testid="portfolio-save">
              <Check size={16} /> {busy ? "…" : "Save"}
            </button>
            <button className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="portfolio-empty">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="portfolio-empty">
          {isMine ? "No projects yet — add your first one." : "No projects yet."}
        </div>
      ) : (
        <div className="portfolio-grid">
          {projects.map((p) => (
            <article key={p.id} className="portfolio-card" data-testid={`portfolio-card-${p.id}`}>
              {p.main_image_url && (
                <div className="portfolio-card-cover">
                  <img src={p.main_image_url} alt={p.title} />
                </div>
              )}
              <div className="portfolio-card-body">
                <h3 className="portfolio-card-title">{p.title}</h3>
                {p.summary && <p className="portfolio-card-summary">{p.summary}</p>}
                {p.link && (
                  <a href={p.link} target="_blank" rel="noopener noreferrer" className="portfolio-card-link">
                    Visit →
                  </a>
                )}
                {isMine && (
                  <div className="portfolio-card-actions">
                    <button className="btn-ghost" onClick={() => startEdit(p)} data-testid={`portfolio-edit-${p.id}`}>
                      <Edit3 size={14} /> Edit
                    </button>
                    <button className="btn-ghost btn-danger" onClick={() => remove(p.id)} data-testid={`portfolio-delete-${p.id}`}>
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default Portfolio;
